import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { catchError, map, of, switchMap, take } from 'rxjs';
import {
  AuthService,
  SessionService,
  LoggingService,
  ProjectService,
  ProjectErrorHandlerService
} from '@newroom-connect/library/services';

import { NavigationService } from '../../services/navigation.service';
import { CodeLoginGuard } from '../code-login/code-login.guard';

/**
 * Guard that checks if the user is authenticated.
 * Attempts to refresh the session if needed before checking authentication status.
 *
 * @param route The activated route snapshot containing route parameters.
 * @param state
 * @returns Observable that resolves to true if authenticated, or UrlTree for redirect.
 */
export const AuthenticationGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const sessionService = inject(SessionService);
  const loggingService = inject(LoggingService);
  const navigationService = inject(NavigationService);
  const projectService = inject(ProjectService);
  const projectErrorHandlerService = inject(ProjectErrorHandlerService);

  const projectSlug = route.params['projectSlug'];
  const code = route.queryParams['code'];
  const username = route.queryParams['username'];
  const refreshToken = route.queryParams['refreshToken'];

  const userSession = sessionService.getUserSession();
  const isAuthenticated = userSession?.isValid() ?? false;
  const isAuthPage = (route.routeConfig?.path ?? '').includes('signin') || (route.routeConfig?.path ?? '').includes('signup');

  // Handle special case for 404 page.
  if (projectSlug === '404') {
    return of(true);
  }

  // 1. Handle Signin routes case except for when code parameter is present.
  if (isAuthPage && !code) {
    // If in signin route and not logged in or there is code parameter, we allow access code login in handled by sign in page.
    if (!isAuthenticated) {
      return of(true);
    }

    return authService.refreshSessionIfNeeded().pipe(
      catchError(() => {
        sessionService.clearUserSession();
        return of(false);
      }),
      switchMap(() => projectService.getProjectSigninConfig(projectSlug).pipe(
        catchError(error => {
          loggingService.error('Error fetching project signin config', error);
          navigationService.navigateTo404();
          return of(null);
        }),
        switchMap(projectSignInConfig => {
          // If project is public, let sign in page handle the login.
          if (projectSignInConfig?.isPublic) {
            return of(true);
          }

          // Priority for area redirection:
          //  1. Session Redirect Area - Extracted from token.
          //  2. Project Start Area.
          const currentSession = sessionService.getUserSession();

          const redirectArea = currentSession?.getRedirectArea() || projectSignInConfig?.startAreaSlug;

          if (!projectSignInConfig || !redirectArea) {
            projectErrorHandlerService.handleGetError();
            return of(false);
          }

          navigationService.navigateToAreas(projectSlug, redirectArea);
          return of(false);
        }),
        take(1)
      )),
      take(1)
    );
  }

  // 2. If code parameter is present, delegate the code login handling to the code login guard.
  // This takes precedence over session refresh to ensure code-based re-login is always used.
  if (code) {
    return CodeLoginGuard(route, state);
  }

  // 3. If user is logged in allow access.
  if (isAuthenticated) {
    return of(true);
  }

  // 4. Handle the refreshToken validation, needed for previewing areas from the Studio to the suite.
  return authService.refreshSessionIfNeeded().pipe(
    switchMap((loginResponse) => {
      if (loginResponse) {
        return of(true);
      }

      if (refreshToken && username) {
        return authService.validatePreviewToken(refreshToken, username).pipe(
          map(response => {
            if (!response?.tokenSet || !response?.sessionId) {
              return true;
            }

            // Do not use redirect area for previewing areas from the Studio to the suite.
            sessionService.setUserSession(response.tokenSet, response.sessionId, username, sessionService.getUserSession()?.getRememberMe(), null, true, false);
            navigationService.navigateToCleanUrl(state.url);
            return false;
          }),
          catchError(error => {
            loggingService.error(error);
            return of(true);
          })
        );
      }

      // Use NavigationService for consistent navigation
      navigationService.navigateToSignIn(projectSlug);
      return of(false);
    }),
    catchError((error: unknown) => {
      loggingService.error('Authentication check failed:', error);

      // Clear invalid session
      sessionService.clearUserSession();

      // Use NavigationService for consistent navigation
      navigationService.navigateToSignIn(projectSlug);
      return of(false);
    })
  );
};
